﻿import React, { useState, useEffect } from 'react'
import { withStyles, createStyles, } from '@mui/styles'
import { Grid, Paper, Tooltip, Typography } from '@mui/material'
import { Add, CheckBox, CheckBoxOutlineBlank, DeleteOutlined, FileCopyOutlined, History } from '@mui/icons-material'
import { defaultStyles, muiOptions, defaultColors } from './../../infrastructure/materialUiThemeProvider'
import guid, { Guid } from '../../infrastructure/guid'
import { DataTable, CustomDialog, Button } from '../common/customComponents'
import { api, get, post } from '../../infrastructure/api'
import {
    DealListItem, DealMovementType, SearchFields, DutyStatus, DuplicateDealCommand, DealPricingType,
    GetMovementType, DealStatus, PricingPeriodListItem, DealListItemBase, Deal,
} from './dealModels'
import { t } from '../../infrastructure/i18nextHelper'
import { DealContainer, DealDialogContainer } from './dealEdit/dealEditStore'
import { SearchField, DatePicker, MultipleSelect, Switch, MonthRangePicker, GroupedMultiSelect } from '../common/customComponents'
import { historyDialog } from '../common/history/dialog'
import moment from 'moment'
import { popupNavigator } from '../../infrastructure/popupNavigator'
import { hasFeature } from '../../infrastructure/feature'
import { ActionDescriptor, GroupDescriptor } from '../common/components/table/table'
import { exportItems } from './dealListExporter'
import { hasClaim } from '../../infrastructure/signIn/userContext'
import { Claims } from '../../infrastructure/signIn/models'
import { ExcelGeneratorContainer } from '../../infrastructure/excelExport'
import { useActionDebounceWithArgs } from '../common/debounce'
import { setInLocalStorage } from '../../infrastructure/localStorage'
import { DuplicateDealDialog, DealDuplicatePeriod } from '../common/duplicate/duplicateDealDialog'
import { useDealList } from './dealListStore'

let emptySearchFields = (): SearchFields => ({
    sites: [],
    companies: [],
    counterparties: [],
    products: [],
    dealAssignees: [],
    dealTypes: [],
    statuss: [],
    moTs: [],
    pricingTypes: [],
    formulaTypes: []
})

let roundNumber = (value: number | null): number | null => value ? Math.round(value) : null
let isBetween = (value: number | null, min: number | null, max: number | null): boolean =>
    value != null && (value >= (min ?? 0)) && (value <= (max ?? 0))
let getToleranceOptionLabel = (value: string | null): string | null => value && t(`deals.label.${value}`)
let roundDecimal = (value: number | null): number | null => value ? Math.round(value * 100) / 100 : null

let toTableItemBase = (dealItem: DealListItemBase): DealListItemBase => {
    return {
        id: dealItem.id,
        type: dealItem.type,
        movementType: dealItem.movementType,
        referenceNumber: dealItem.referenceNumber,
        dutyStatus: dealItem.dutyStatus,
        company: dealItem.company,
        counterparty: dealItem.counterparty,
        validFrom: dealItem.validFrom,
        validTo: dealItem.validTo,
        productCode: dealItem.productCode,
        site: dealItem.site,
        contractReference: dealItem.contractReference,
        meanOfTransportation: dealItem.meanOfTransportation,
        signed: dealItem.signed,
        assigneeFullName: dealItem.assigneeFullName,
        das: dealItem.das,
        dealNumber: dealItem.dealNumber,
        exchangeNumber: dealItem.exchangeNumber,
        frontOfficerFullName: dealItem.frontOfficerFullName,
        pricingType: dealItem.pricingType,
        sapHandlingType: dealItem.sapHandlingType,
        sapPlant: dealItem.sapPlant,
        sapValuationType: dealItem.sapValuationType,
        sapVendor: dealItem.sapVendor,
        shipTo: dealItem.shipTo,
        status: dealItem.status,
        termSpot: dealItem.termSpot,
        generateMktSaleForecasts: dealItem.generateMktSaleForecasts,
        volume: roundNumber(dealItem.volume),
        underToleranceQuantity: roundNumber(dealItem.underToleranceQuantity),
        overToleranceQuantity: roundNumber(dealItem.overToleranceQuantity),
        isContractFromSap: dealItem.isContractFromSap,
        formulaType: dealItem.formulaType,
        premiumUnit: dealItem.premiumUnit,
    }
}

let toDealListTableItem = (dealItem: DealListItem): DealListItem => {
    return {
        ...toTableItemBase(dealItem),
        nextValidator: dealItem.nextValidator,
        assignee: dealItem.assignee,
        currency: dealItem.currency,
        dealConsumption: dealItem.dealConsumption,
        frontOfficer: dealItem.frontOfficer,
        movementQuantity: roundNumber(dealItem.movementQuantity),
        overTolerance: dealItem.overTolerance,
        quantity: roundNumber(dealItem.quantity),
        quantityPriceUnit: dealItem.quantityPriceUnit,
        sapQuantity: roundNumber(dealItem.sapQuantity),
        toleranceOption: getToleranceOptionLabel(dealItem.toleranceOption),
        underTolerance: dealItem.underTolerance,
        unit: dealItem.unit,
        isQuantityInTolerance: isBetween(dealItem.movementQuantity, dealItem.underToleranceQuantity, dealItem.overToleranceQuantity),
        companyCurrency: dealItem.companyCurrency,
        indexPriceUnit: dealItem.indexPriceUnit,
        productQuantitytUnit: dealItem.productQuantitytUnit,
        provisionalIndexPriceUnit: dealItem.provisionalIndexPriceUnit,
        unitPrice: roundDecimal(dealItem.unitPrice),
        premium: dealItem.premium,
        unitPricePlusPremium: roundDecimal(dealItem.unitPricePlusPremium),
        quantityUnitPrice: roundDecimal(dealItem.quantityUnitPrice),
        transportFees: roundDecimal(dealItem.transportFees),
        storageFees: roundDecimal(dealItem.storageFees),
        dealUnitPrice: roundDecimal(dealItem.dealUnitPrice),
        exchangeRate: dealItem.exchangeRate,
        amount: roundNumber(dealItem.amount),
        amountSap: roundNumber(dealItem.amountSap),
        paymentTerms: dealItem.paymentTerms,
        paymentTermsDate: dealItem.paymentTermsDate,
        provisionalUnitPrice: roundDecimal(dealItem.provisionalUnitPrice),
        provisionalPremium: dealItem.provisionalPremium,
        provisionalUnitPricePlusPremium: roundDecimal(dealItem.provisionalUnitPricePlusPremium),
        provisionalQuantityUnitPrice: roundDecimal(dealItem.provisionalQuantityUnitPrice),
        provisionalTransportFees: roundDecimal(dealItem.provisionalTransportFees),
        provisionalStorageFees: roundDecimal(dealItem.provisionalStorageFees),
        provisionalDealUnitPrice: roundDecimal(dealItem.provisionalDealUnitPrice),
        provisionalExchangeRate: dealItem.provisionalExchangeRate,
        provisionalAmount: roundNumber(dealItem.provisionalAmount),
        provisionalAmountSap: roundNumber(dealItem.provisionalAmountSap)
    }
}

let toPricingPeriodTableItem = (pricingPeriodItem: PricingPeriodListItem): PricingPeriodListItem => {
    return {
        ...toTableItemBase(pricingPeriodItem),
        priceIndexOrder: pricingPeriodItem.priceIndexOrder,
        pricingPeriodOrder: pricingPeriodItem.pricingPeriodOrder,
        priceIndexName: pricingPeriodItem.priceIndexName,
        pricingStartDate: pricingPeriodItem.pricingStartDate,
        pricingEndDate: pricingPeriodItem.pricingEndDate,
        premium: pricingPeriodItem.premium,
        pricedDealVolume: signedVolume(pricingPeriodItem.movementType, pricingPeriodItem.pricedDealVolume),
        pricedMovementVolume: signedVolume(pricingPeriodItem.movementType, pricingPeriodItem.pricedMovementVolume),
        pricedSapQuantity: signedVolume(pricingPeriodItem.movementType, pricingPeriodItem.pricedSapQuantity),
        pricedDealQuantity: signedVolume(pricingPeriodItem.movementType, pricingPeriodItem.pricedDealQuantity),
        percentage: roundNumber(pricingPeriodItem.percentage * 100) ?? 0,
        key: pricingPeriodItem.id.toString() + pricingPeriodItem.priceIndexOrder.toString() + pricingPeriodItem.pricingPeriodOrder.toString(),
        isQuantityInTolerance: isBetween(pricingPeriodItem.pricedMovementVolume, pricingPeriodItem.underToleranceQuantity, pricingPeriodItem.overToleranceQuantity)
    }
}

let signedVolume = (movementType: string | null, volume: number | null) => {
    let roundedVolume = roundNumber(volume)
    return roundedVolume && movementType === DealMovementType.SALE
        ? -roundedVolume
        : roundedVolume
}

export type DealListFilters = {
    referenceNumber: string | null
    dealNumber: string | null
}

let noFilters: DealListFilters = {
    referenceNumber: null,
    dealNumber: null
}

function DealList({ classes }) {
    let [searchFields, setSearchFields] = useState<SearchFields>(emptySearchFields)
    let [dutyStatuses, setDutyStatuses] = useState<DutyStatus[]>([])
    let [deleteDealConfirmIsOpen, setDeleteDealConfirmIsOpen] = useState<boolean>(false)
    let [deleteDealSimpleConfirmIsOpen, setDeleteDealSimpleConfirmIsOpen] = useState<boolean>(false)
    let [toDeleteDealIds, setToDeleteDealIds] = useState<Guid[]>([])
    let [searchQuery, setSearchQuery] = useState<string>('')
    let [dealItems, setDealItems] = useState<DealListItem[]>([])
    let [pricingPeriodItems, setPricingPeriodItems] = useState<PricingPeriodListItem[]>([])
    let [selectedDeals, setSelectedDeals] = useState<DealListItem[]>([])
    let [selectedPricingPeriods, setSelectedPricingPeriods] = useState<PricingPeriodListItem[]>([])
    let [duplicateConfirmDialogOpen, setDuplicateConfirmDialogOpen] = useState<boolean>(false)
    let [currentDealId, setcurrentDealId] = useState<Guid | null>(null)
    let [dealListFilters, setDealListFilters] = useState<DealListFilters>(noFilters)

    let dealContainer = DealDialogContainer.useContainer()
    let dealStore = DealContainer.useContainer()
    let excelGenerator = ExcelGeneratorContainer.useContainer()

    const termSpot = ['Term', 'Spot']
    const signedNotSigned = ['Signed', 'Not Signed']

    let filters = dealStore.filters

    let getDealListItems = () => applyFilters<DealListItem>(dealItems).map(toDealListTableItem)
    let getPricingPeriodListItems = () => applyFilters<PricingPeriodListItem>(pricingPeriodItems).map(toPricingPeriodTableItem)

    let openPopup = (targetId: Guid, pricingType?: DealPricingType) => {
        popupNavigator.open('deal', targetId, { pricingType: pricingType })
    }

    let loadDeals = async () => {
        let filtersQuery = constructFiltersQuery()
        let route = filtersQuery ? `deal?${filtersQuery}` : 'deal'
        var deals = await get<DealListItem[]>(route)
        setDealItems(deals)
    }

    let loadPricingPeriods = async () => {
        let filtersQuery = constructFiltersQuery()
        let route = filtersQuery ? `deal/pricingPeriod?${filtersQuery}` : 'deal/pricingPeriod'
        let pricingPeriods = await get<PricingPeriodListItem[]>(route)
        setPricingPeriodItems(pricingPeriods)
    }

    let loadDealSearchFields = async () => {
        let searchFields = await get<SearchFields>('deal/searchField')
        setSearchFields(searchFields)

        let dutyStatuses = searchFields.companies.map(x => x.dutyStatuses).reduce((a, b) => a.concat(b)).distinct()
        setDutyStatuses(dutyStatuses)
    }

    let saveFiltersInLocalStorage = () => {
        let filtersLength = Object.keys(filters).length

        if (filtersLength > 0)
            setInLocalStorage('dealFilters', filters)
    }

    let changeDate = (newDate: string | null, key) => {
        dealStore.setFilters((prevFilters) => ({
            ...prevFilters,
            [key]: newDate
        }))
    }

    let constructFiltersQuery = () => {
        let filtersToApply = filters
        let query = ''
        let arrays = ['dealTypes', 'movementTypes', 'termSpot',
            'companys', 'productIds', 'sites', 'moTs',
            'dutyStatuss', 'counterpartyIds', 'statuss',
            'signedNotSigned', 'dealAssignees', 'frontOfficers', 'pricingTypes', 'formulaTypes']

        let filterProperties = Object.keys(filtersToApply).filter(x => arrays.indexOf(x) === -1)
        let initializedFilters = filterProperties.filter(x =>
            (filtersToApply[x] !== undefined && filtersToApply[x] !== null))

        initializedFilters.map(x => query = query + `${x}=${filtersToApply[x]}&`)

        arrays.forEach(prop => {
            if (filtersToApply[prop] && filtersToApply[prop].length > 0)
                filtersToApply[prop].map(value => query = query + `${prop}=${value}&`)
        })

        return query
    }

    let applyFilters = <T extends DealListItemBase>(deals: T[]): T[] => {
        if (dealListFilters.referenceNumber)
            deals = deals.filter(x => x.referenceNumber?.toLowerCase().contains(dealListFilters.referenceNumber!.toLowerCase()))
        if (dealListFilters.dealNumber)
            deals = deals.filter(x => x.dealNumber?.toString().contains(dealListFilters.dealNumber!.toString()))
        return deals
    }

    let onSelect = async (deal: DealListItemBase) => {
        openPopup(deal.id)
    }

    let onDeleteDeals = <T extends DealListItemBase>(selectedItems: T[]) => {
        setToDeleteDealIds(selectedItems.map(x => x.id).distinct())

        let shouldNotAllowMovementToStay = hasFeature('BatchDailyMovementCreationFromDeal') &&
            selectedItems.some(x => x.meanOfTransportation === 'Road' && x.movementType === DealMovementType.PURCHASE || x.generateMktSaleForecasts)

        if (shouldNotAllowMovementToStay)
            setDeleteDealSimpleConfirmIsOpen(true)
        else
            setDeleteDealConfirmIsOpen(true)
    }

    let deleteDeals = async (deleteMovements: boolean) => {
        setDeleteDealConfirmIsOpen(false)
        setDeleteDealSimpleConfirmIsOpen(false)
        await post('deal/batchDelete', { dealIds: toDeleteDealIds, deleteMovements: deleteMovements })
        filters.displayPricingPeriodList ? await loadPricingPeriods() : await loadDeals()
    }

    useEffect(() => {
        if (!dealContainer.isOpen && dealContainer.previousIsOpen)
            filters.displayPricingPeriodList ? loadPricingPeriods() : loadDeals()
    }, [dealContainer.isOpen])

    useEffect(() => {
        saveFiltersInLocalStorage()
        filters.displayPricingPeriodList ? loadPricingPeriods() : loadDeals()
        loadDealSearchFields()
        setSearchQuery(filters.query ?? '')
    }, [filters])

    useEffect(() => {
        let currentDeal = dealItems.find(x => x.id === currentDealId)
        let period: DealDuplicatePeriod = {
            start: dealStore.isPeriodMode ? dealStore.addOneMonth(currentDeal?.validFrom!) : moment(currentDeal?.validFrom).format('YYYY/MM/DD'),
            end: dealStore.isPeriodMode ? dealStore.addOneMonth(currentDeal?.validTo!) : moment(currentDeal?.validTo).format('YYYY/MM/DD')
        }
        dealStore.setDuplicatePeriod(period)
    }, [duplicateConfirmDialogOpen])

    let onValidatedQuery = () => dealStore.setFilters({ ...filters, query: searchQuery })

    let onQueryChanged = (e) => {
        if (e.key === 'Enter')
            return onValidatedQuery()
        else
            setSearchQuery(e.target.value)
    }

    let exportListItems = async () => {
        let items: DealListItemBase[] = filters.displayPricingPeriodList
            ? selectedPricingPeriods.length > 0 ? selectedPricingPeriods : pricingPeriodItems
            : selectedDeals.length > 0 ? selectedDeals : dealItems
        let columns = filters.displayPricingPeriodList
            ? pricingPeriodColumns
            : dealListColumns

        exportItems(columns, items, excelGenerator.generate, filters.displayPricingPeriodList)
    }

    let duplicateDeal = async (selectedDealId: Guid): Promise<void> => {
        let deal = dealItems.find(x => x.id === selectedDealId)
        let command: DuplicateDealCommand = {
            sourceId: selectedDealId,
            duplicateId: guid.createNew(),
            start: dealStore.isPeriodMode ? dealStore.duplicatePeriod?.start! : moment(deal?.validFrom!).format('YYYY/MM/DD'),
            end: dealStore.isPeriodMode ? dealStore.duplicatePeriod?.end! : moment(deal?.validTo!).format('YYYY/MM/DD')
        }
        await dealStore.duplicateDeal(command)
        openPopup(command.duplicateId)
    }

    let updateSapQuantity = async (dealId: Guid) => {
        await api.post('sapApi/updateDealSapQuantity ', { dealId: dealId })
        let deal = await get<Deal>(`deal/${dealId}`)
        let newDealItems = dealItems.map(x => x.id === dealId ? { ...x, sapQuantity: deal.sapQuantity } : x)
        setDealItems([...newDealItems])
    }

    let handleDuplicateDebouncer = useActionDebounceWithArgs(duplicateDeal)

    let hasOneCompany = searchFields.companies.map(x => x.code).distinct().length === 1

    let dealListItems = getDealListItems()
    let totalVolume = roundNumber(dealListItems.reduce((acc, curr) => acc + (curr.volume ?? 0), 0))
    let totalMvtQuantity = roundNumber(dealListItems.reduce((acc, curr) => acc + (curr.movementQuantity ?? 0), 0))
    let totalQuantity = roundNumber(dealListItems.reduce((acc, curr) => acc + (curr.quantity ?? 0), 0))
    let totalSapQuantity = roundNumber(dealListItems.reduce((acc, curr) => acc + (curr.sapQuantity ?? 0), 0))

    let pricingPeriodListItems = getPricingPeriodListItems()
    let totalPricedDealVolume = roundNumber(pricingPeriodListItems.reduce((acc, curr) => acc + (curr.pricedDealVolume ?? 0), 0))
    let totalPricedMovementVolume = roundNumber(pricingPeriodListItems.reduce((acc, curr) => acc + (curr.pricedMovementVolume ?? 0), 0))
    let totalPricedSapQuantity = roundNumber(pricingPeriodListItems.reduce((acc, curr) => acc + (curr.pricedSapQuantity ?? 0), 0))
    let totalPricedDealQuantity = roundNumber(pricingPeriodListItems.reduce((acc, curr) => acc + (curr.pricedDealQuantity ?? 0), 0))

    const hasFeatureUpdateSapVolume = hasFeature('DealsSapQuantityVolumeUpdate')
    const hasFeatureGenericPricing = hasFeature('GenericPricing')
    const hasFeatureProvitionalPricing = hasFeature('ProvisionalPricing')
    const hasFeatureFormulaType = hasFeature('FormulaType')

    let setNewDealListFilters = (newFilters: DealListFilters) => setDealListFilters({ ...dealListFilters, ...newFilters })

    const { dealListColumns, pricingPeriodColumns } = useDealList(
        classes, dealListFilters, setNewDealListFilters,
        hasFeatureGenericPricing, hasFeatureProvitionalPricing, hasFeatureUpdateSapVolume, hasFeatureFormulaType,
        totalVolume, totalMvtQuantity, totalSapQuantity, totalQuantity, totalPricedDealVolume, totalPricedMovementVolume,
        totalPricedSapQuantity, totalPricedDealQuantity,
        hasOneCompany, dealStore.isInternal, dealStore.isPaperTransaction, updateSapQuantity
    )

    let dealsTableActions = <T extends DealListItemBase>(): ActionDescriptor<T>[] => {
        return [
            { name: 'Delete', action: onDeleteDeals, icon: <DeleteOutlined />, isHidden: !hasClaim(Claims.DealManager) },
            {
                name: 'History',
                action: (x: T) =>
                    historyDialog.open({
                        id: x.id, type: 'deal', name: `
                            ${x.referenceNumber ?? ''}
                            ${x.company ?? ''}
                            ${x.dutyStatus ?? ''}
                            ${x.productCode ?? ''}
                            ${x.volume ? x.volume + ' M20' : ''}`
                    }),
                isBodyAction: true, icon: <History />
            },
            {
                name: 'Duplicate', action: (x: T) => { setcurrentDealId(x.id); setDuplicateConfirmDialogOpen(true) },
                icon:
                    <Tooltip title={<Typography variant='body1'>{t('deals.label.duplicateDeal')}</Typography>} placement='top'>
                        <FileCopyOutlined />
                    </Tooltip>,
                isBodyAction: true,
                disabled: (x: T) => !hasFeature('CreateDeal') || !hasClaim(Claims.DealManager) || x.type === 'INTERNAL'
            }
        ]
    }

    let details = (span: number): GroupDescriptor => {
        return { name: 'details', title: '', colSpan: span }
    }
    let header = { name: 'header', title: t('deals.label.header'), withHiddenColumns: true }
    let quantityDetails = { name: 'quantityDetails', title: t('deals.label.quantityDetails'), withHiddenColumns: true }
    let sapDetails = { name: 'sapDetails', title: t('deals.label.sapDetails'), withHiddenColumns: true }
    let priceDetails = { name: 'priceDetails', title: t('deals.label.priceDetails'), withHiddenColumns: true }
    let organizationDetails = { name: 'organizationnalDetails', title: t('deals.label.organizationnalDetails'), withHiddenColumns: true }
    let pricingDetails = { name: 'pricingDetails', title: t('deals.label.pricingDetails'), withHiddenColumns: true }
    let provisionalPricingDetails = { name: 'provisionalPricingDetails', title: t('deals.label.provisionalPricingDetails'), withHiddenColumns: true }
    let options = { name: 'options', title: '' }

    let dealsTableGroups: GroupDescriptor[] =
        [
            ...[details(hasOneCompany ? 0 : 1), header, quantityDetails, sapDetails, priceDetails, organizationDetails],
            ...hasFeatureGenericPricing ? [pricingDetails] : [],
            ...hasFeatureGenericPricing && hasFeatureProvitionalPricing ? [provisionalPricingDetails] : [],
            ...[options]
        ]

    let pricingPeriodsTableGroups: GroupDescriptor[] = [
        details(hasOneCompany ? 15 : 16),
        quantityDetails,
        sapDetails,
        organizationDetails,
        options
    ]

    let canCreate = hasFeature('CreateDeal') && hasClaim(Claims.DealManager)

    return (
        <div className={classes.pageContainer}>
            <div className={classes.firstRow}>
                <Typography className={classes.pageTitle} variant='h5' display='block' gutterBottom>{t('deals.name')}</Typography>
                <div>
                    <Paper className={classes.paperFilter}>
                        <MultipleSelect label={t('deals.label.type')} classesOverride={{ form: classes.filterField }}
                            values={filters.dealTypes}
                            choices={searchFields.dealTypes}
                            onChange={x => dealStore.setFilters({ ...filters, dealTypes: x })} />
                        <MultipleSelect label={t('deals.label.movementType')} classesOverride={{ form: classes.filterField }}
                            values={filters.movementTypes}
                            choices={Object.keys(DealMovementType).map(x => { return { value: DealMovementType[x], text: dealStore.getTranslatedMovementType(DealMovementType[x]) } })}
                            onChange={x => dealStore.setFilters({ ...filters, movementTypes: x })} />
                        <MultipleSelect label={t('deals.label.termSpot')} classesOverride={{ form: classes.filterField }}
                            values={filters.termSpot}
                            choices={termSpot}
                            onChange={x => dealStore.setFilters({ ...filters, termSpot: x })} />
                        {!hasOneCompany && <MultipleSelect label={t('deals.label.company')} classesOverride={{ form: classes.filterField }}
                            values={filters.companys}
                            choices={searchFields.companies.map(x => ({ value: x.code, text: x.name }))}
                            onChange={x => dealStore.setFilters({ ...filters, companys: x })} />}
                        <GroupedMultiSelect label={t('deals.label.product')} classesOverride={{ form: classes.filterField }}
                            values={filters.productIds}
                            choices={searchFields.products.map(x => { return { value: x.id, text: x.code, group: x.productGroup ?? x.code } })}
                            onChange={x => dealStore.setFilters({ ...filters, productIds: x })} />
                        <GroupedMultiSelect
                            label={t('deals.label.site')}
                            classesOverride={{ form: classes.filterField }}
                            values={filters.sites}
                            choices={searchFields.sites.map(x => { return { value: x.code, text: x.name, group: x.siteGroup } })}
                            allWhenEmpty={false}
                            onChange={x => dealStore.setFilters({ ...filters, sites: x })} />
                        <MultipleSelect label={t('deals.label.dutyStatus')} hideOnSingleChoice
                            values={filters.dutyStatuss} classesOverride={{ form: classes.filterField }}
                            choices={dutyStatuses}
                            onChange={x => dealStore.setFilters({ ...filters, dutyStatuss: x })} />
                        <MultipleSelect label={t('deals.label.mot')} hideOnSingleChoice
                            values={filters.moTs} classesOverride={{ form: classes.filterField }}
                            choices={searchFields.moTs}
                            onChange={x => dealStore.setFilters({ ...filters, moTs: x })} />
                        <MultipleSelect label={t('deals.label.counterparty')} classesOverride={{ form: classes.filterField }}
                            values={filters.counterpartyIds}
                            choices={searchFields.counterparties.map(x => ({ value: x.id, text: x.name }))}
                            onChange={x => dealStore.setFilters({ ...filters, counterpartyIds: x })} />
                        {hasFeature('FormulaType') && searchFields.formulaTypes.length > 0 &&
                            <MultipleSelect label={t('deals.label.formulaType')} classesOverride={{ form: classes.filterField }}
                                values={filters.formulaTypes}
                                choices={searchFields.formulaTypes.map(x => ({
                                    value: x, text: t(`admin.masterdata.pricingPeriod.formulaType.${x}`)
                                }))}
                                onChange={x => dealStore.setFilters({ ...filters, formulaTypes: x })} />}
                        <MultipleSelect label={t('deals.label.pricingTypes')} classesOverride={{ form: classes.filterField }}
                            values={filters.pricingTypes}
                            choices={searchFields.pricingTypes.map(x => ({ value: x, text: t('deals.label.pricing.' + x) }))}
                            onChange={x => dealStore.setFilters({ ...filters, pricingTypes: x })} />
                        <MultipleSelect label={t('deals.label.status')} classesOverride={{ form: classes.filterField }}
                            values={filters.statuss}
                            choices={searchFields.statuss}
                            onChange={x => dealStore.setFilters({ ...filters, statuss: x })} />
                        <MultipleSelect label={t('deals.label.signed')} classesOverride={{ form: classes.filterField }}
                            values={filters.signedNotSigned}
                            choices={signedNotSigned}
                            onChange={x => dealStore.setFilters({ ...filters, signedNotSigned: x })} />
                        <MultipleSelect label={t('deals.label.assigneeUserName')} classesOverride={{ form: classes.filterField }}
                            values={filters.dealAssignees}
                            choices={searchFields.dealAssignees.map(x => ({ value: x.userName, text: x.fullName }))}
                            onChange={x => dealStore.setFilters({ ...filters, dealAssignees: x })} />
                        <MultipleSelect label={t('deals.label.frontOfficer')} classesOverride={{ form: classes.filterField }}
                            values={filters.frontOfficers}
                            choices={searchFields.dealAssignees.map(x => ({ value: x.userName, text: x.fullName }))}
                            onChange={x => dealStore.setFilters({ ...filters, frontOfficers: x })} />
                    </Paper>
                </div>
            </div>
            <Paper className={classes.root + ' ' + classes.secondRow}>
                <div className={classes.tableHead + ' ' + classes.stickyFilters}>
                    <div className={classes.titleSwitchRow}>
                        <Switch form
                            isChecked={filters.displayPricingPeriodList}
                            changeCallback={() => dealStore.setFilters({ ...filters, displayPricingPeriodList: !filters.displayPricingPeriodList })}
                            offText={t('deals.label.allDeals')}
                            onText={t('deals.label.pricingPeriodList')} />
                    </div>
                    <div className={classes.datePickerContainer}>
                        <Switch
                            isChecked={filters.isDayPeriodMode}
                            changeCallback={() => dealStore.setFilters({ ...filters, isDayPeriodMode: !filters.isDayPeriodMode })}
                            offText={t('deals.label.monthFilter')}
                            onText={t('deals.label.dayFilter')}
                        />
                        <Grid>
                            {filters.isDayPeriodMode
                                ? <DatePicker
                                    date={filters.dateOn || null}
                                    label={t('deals.table.' + (filters.displayPricingPeriodList ? 'priceOn' : 'validOn'))}
                                    disableNewStyle
                                    setDate={newDate => changeDate(newDate, 'dateOn')}
                                    classesOverride={{ datepicker: classes.filterFieldDate }}
                                />
                                : <MonthRangePicker
                                    label={t('deals.table.' + (filters.displayPricingPeriodList ? 'priceIn' : 'validIn'))}
                                    startDate={filters.startDate || null}
                                    endDate={filters.endDate || null}
                                    onStartDateChange={(newDate) => changeDate(newDate, 'startDate')}
                                    onEndDateChange={(newDate) => changeDate(newDate, 'endDate')}
                                    disableNewStyle
                                    classesOverride={{ datepicker: classes.filterFieldDate }}
                                    status="info"
                                />
                            }
                        </Grid>
                    </div>
                    <div className={classes.buttonsRow}>
                        {hasFeature('ExportDealList') && (hasClaim(Claims.DealManager) || hasClaim(Claims.DealReader))
                            ? <Button className={classes.headerButton}
                                label={t('admin.masterdata.export')}
                                img={'/static/images/excel_red.svg'}
                                onClick={exportListItems} />
                            : null}
                        <SearchField label={t('deals.label.search')} onChange={onQueryChanged} onClick={onValidatedQuery} text={searchQuery} />
                        {
                            canCreate
                            && <Button className={classes.createButton}
                                label={t('deals.table.createNewFormula')}
                                img={<Add />}
                                onClick={() => openPopup(guid.createNew() as Guid, 'Formula')} />}
                        {
                            canCreate && hasFeature('PricingTrigger')
                            && <Button
                                label={t('deals.table.createNewTrigger')}
                                img={<Add />}
                                onClick={() => openPopup(guid.createNew() as Guid, 'Trigger')} />
                        }
                        {
                            canCreate && hasFeature('PricingFixedPrice')
                            && <Button
                                label={t('deals.table.createNewFixed')}
                                img={<Add />}
                                onClick={() => openPopup(guid.createNew() as Guid, 'Fixed')} />
                        }
                    </div>
                </div>
                {filters.displayPricingPeriodList
                    ?
                    <DataTable
                        tableId={'pricing-period-table'}
                        className={classes.stickyHeader}
                        items={getPricingPeriodListItems()}
                        idSelector={(x: PricingPeriodListItem) => x.key}
                        onClick={onSelect}
                        columns={pricingPeriodColumns}
                        actions={dealsTableActions<PricingPeriodListItem>()}
                        groups={pricingPeriodsTableGroups}
                        onSelectionChange={(x: PricingPeriodListItem[]) => setSelectedPricingPeriods(x)}
                    />
                    : <DataTable
                        tableId={'deal-table'}
                        className={classes.stickyHeader}
                        items={getDealListItems()}
                        idSelector={(x: DealListItem) => x.id}
                        onSelectionChange={(x: DealListItem[]) => setSelectedDeals(x)}
                        onClick={onSelect}
                        columns={dealListColumns}
                        actions={dealsTableActions<DealListItem>()}
                        groups={dealsTableGroups} />
                }
            </Paper>
            <CustomDialog isOpen={deleteDealConfirmIsOpen} yesNo
                title={t('deals.table.confirmDelete.title')}
                contentText={t('deals.table.confirmDelete.content')}
                content2Text={t('deals.table.confirmDelete.content2')}
                yesButtonText={t('deals.table.confirmDelete.yes')}
                noButtonText={t('deals.table.confirmDelete.no')}
                onYes={() => deleteDeals(true)}
                onNo={() => deleteDeals(false)}
                onCancel={() => setDeleteDealConfirmIsOpen(false)}
                onClose={() => setDeleteDealConfirmIsOpen(false)}
            />
            <CustomDialog isOpen={deleteDealSimpleConfirmIsOpen}
                title={t('deals.table.simpleConfirmDelete.title')}
                contentText={t('deals.table.simpleConfirmDelete.content')}
                confirmButtonText={t('deals.table.simpleConfirmDelete.confirm')}
                onConfirm={() => deleteDeals(true)}
                onCancel={() => setDeleteDealSimpleConfirmIsOpen(false)}
                onClose={() => setDeleteDealSimpleConfirmIsOpen(false)}
            />
            <DuplicateDealDialog
                isOpen={duplicateConfirmDialogOpen}
                isPeriodMode={dealStore.isPeriodMode}
                onPeriodModeChange={dealStore.setIsPeriodMode}
                setIsOpen={setDuplicateConfirmDialogOpen}
                startDate={dealStore.duplicatePeriod?.start}
                endDate={dealStore.duplicatePeriod?.end}
                isDuplicateByPeriodNotRestricted={dealStore.isDuplicateByPeriodAndNotInternal}
                onConfirm={async () => {
                    setDuplicateConfirmDialogOpen(false)
                    if (currentDealId) await handleDuplicateDebouncer.execute(currentDealId)
                }}
                onDealPeriodChange={(x: DealDuplicatePeriod) => dealStore.onDealPeriodChange(x)} />
        </div >
    )
}

let styles = _ =>
    createStyles({
        firstRow: {
            ...defaultStyles.flexRow,
            justifyContent: 'space-between',
            alignItems: 'stretch',
            width: '100%',
            gridArea: 'firstRow',
            whiteSpace: 'nowrap'
        },
        secondRow: {
            gridArea: 'secondRow'
        },
        titleSwitchRow: {
            ...defaultStyles.flexRow,
        },
        pageTitle: {
            color: defaultColors.grey.dark.color,
            alignSelf: 'flex-start',
            marginRight: '2em'
        },
        tableHead: {
            ...defaultStyles.flexRow,
            justifyContent: 'space-between',
            alignItems: 'stretch',
            padding: '0.2em 1%',
            width: '100%'
        },
        paperTitle: {
            margin: '0',
            marginRight: '6em',
            color: defaultColors.red.main.color
        },
        filterField: {
            width: '8.3em',
            margin: '0em 0.5em'
        },
        filterFieldDate: {
            width: '10.3em',
            margin: '0em 0.5em'
        },
        paperFilter: {
            height: 'auto',
            padding: '0.5em',
        },
        root: {
            flexGrow: 2,
            width: '100%',
            overflow: 'auto'
        },
        headerButton: {
            ...defaultStyles.secondaryButton,
            marginRight: '2em',
        },
        pageContainer: {
            display: 'grid',
            height: '100%',
            paddingBottom: '0.5em',
            'grid-template-areas': "'firstRow' 'secondRow'",
            'grid-template-columns': 'auto',
            'grid-template-rows': '4em minmax(0, 1fr)',
            'grid-gap': '0.8em',
            'overflow-y': 'hidden',
            'box-sizing': 'border-box',
            '&> div': {
                height: '100%',
                width: '100%'
            }
        },
        buttonsRow: {
            display: 'flex',
            alignItems: 'center',
            '& > *:not(:last-child)': {
                marginRight: '1em'
            }
        },
        sapButton: {
            '& button': {
                ...defaultStyles.secondaryButton,
                minWidth: '8em',
                height: '8em',
                width: '8em',
                padding: '0px',
            }
        },
        createButton: {
            ...defaultStyles.primaryButton
        },
        iconsActive: {
            color: defaultColors.lightBlue.main.color
        },
        datePickerContainer: {
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
        },
        positive: {
            color: defaultColors.green.light.color
        },
        stickyFilters: {
            position: 'sticky',
            top: 0,
            zIndex: 3,
            backgroundColor: 'white'
        },
        stickyHeader: {
            position: 'sticky',
            top: '6em',
            zIndex: 2
        }
    })

export default withStyles(styles, muiOptions)(DealList)